import { appGetState } from 'legacy/lut/store'

import { getTokens } from 'core/classes/helpers/StorageHelper'
import settings from 'src/config/settings'

import type { CommitmentTypes } from '../model/enums'
import type { AcknowledgmentCommitmentItem, AcknowledgmentOffering } from '../model/types'

import { recordAcknowledgmentCall } from './recordAcknowledgmentCall'

type AcknowledgmentPlanKey = {
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
  commitmentType: CommitmentTypes
}

type AcknowledgmentEntryKey = AcknowledgmentPlanKey & {
  entryId: number
}

export type AcknowledgmentSigner = {
  email: string
  firstName?: string
  lastName?: string
  role?: string
  contactId?: string
  isOptional?: number
}

type AcknowledgmentActionResponse = {
  status?: 'success' | 'fail' | 'error'
  message?: string
}

/** The commitment row an action acts on, in the shape every typed action takes. */
export const toAcknowledgmentEntryKey = (
  item: AcknowledgmentCommitmentItem
): AcknowledgmentEntryKey => ({
  partnerId: item.PartnerId,
  fYear: item.FYear,
  programType: item.ProgramType,
  commitmentType: item.CommitmentType,
  entryId: item.EntryId,
})

const postAcknowledgmentAction = async (
  action: string,
  body: object,
  debugSource?: string | null
): Promise<void> => {
  const [token] = getTokens(appGetState().app.ownerId)
  const url = `${settings.httpsServices.partner360}/api/v0/acknowledgment/${action}`

  await recordAcknowledgmentCall(
    {
      name: `acknowledgment/${action}`,
      method: 'POST',
      url,
      body,
      target: 'useAcknowledgmentActions · reload()',
      source: debugSource,
    },
    async () => {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      })

      const result = (await response.json().catch(() => ({}))) as AcknowledgmentActionResponse

      if (!response.ok || result.status !== 'success') {
        throw new Error(
          result.message || `Acknowledgment ${action} failed with status ${response.status}`
        )
      }

      return result
    }
  )
}

/* Every action takes the place it is made from last, for the network data window. */
type TDebugSource = string | null | undefined

export const signOffAcknowledgment = (entry: AcknowledgmentEntryKey, source?: TDebugSource) =>
  postAcknowledgmentAction('signOff', entry, source)

export const refuseAcknowledgment = (
  entry: AcknowledgmentEntryKey,
  comment: string,
  source?: TDebugSource
) => postAcknowledgmentAction('refuse', { ...entry, comment }, source)

export const dropAcknowledgment = (
  entry: AcknowledgmentEntryKey,
  comment: string,
  source?: TDebugSource
) => postAcknowledgmentAction('drop', { ...entry, comment }, source)

export const resendAcknowledgment = (entry: AcknowledgmentEntryKey, source?: TDebugSource) =>
  postAcknowledgmentAction('resend', entry, source)

export const addAcknowledgmentSigner = (
  plan: AcknowledgmentPlanKey,
  signer: AcknowledgmentSigner,
  source?: TDebugSource
) => postAcknowledgmentAction('addSigner', { ...plan, ...signer }, source)

export const changeAcknowledgmentSigner = (
  entry: AcknowledgmentEntryKey,
  signer: AcknowledgmentSigner,
  source?: TDebugSource
) => postAcknowledgmentAction('changeSigner', { ...entry, ...signer }, source)

export const selectAcknowledgmentSigner = (
  entry: AcknowledgmentEntryKey,
  signer: AcknowledgmentSigner,
  source?: TDebugSource
) => postAcknowledgmentAction('selectSigner', { ...entry, ...signer }, source)

export const removeAcknowledgmentSigner = (entry: AcknowledgmentEntryKey, source?: TDebugSource) =>
  postAcknowledgmentAction('removeSigner', entry, source)
